// This hack is needed to import an API via a remote script
/// <reference path="widgetApiModule.d.ts" />
var __asyncValues = (this && this.__asyncValues) || function (o) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var m = o[Symbol.asyncIterator], i;
    return m ? m.call(o) : (o = typeof __values === "function" ? __values(o) : o[Symbol.iterator](), i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function () { return this; }, i);
    function verb(n) { i[n] = o[n] && function (v) { return new Promise(function (resolve, reject) { v = o[n](v), settle(resolve, reject, v.done, v.value); }); }; }
    function settle(resolve, reject, d, v) { Promise.resolve(v).then(function(v) { resolve({ value: v, done: d }); }, reject); }
};
import log from "loglevel";
import { addMarkerToPageTimings, Measures } from "../../../telemetry/Measures";
import { TelemetryData } from "../../../telemetry/TelemetryData";
import { Trace } from "../../../telemetry/Trace";
import { dashboard } from "../../../telemetry/TraceLogging";
import { fetchWidgetIntializer } from "../../components/Pages/MainPage/Prefetch";
import { getDefaultAccountType, getHostAuthenticatorApiV5 } from "../../externalApi/AuthenticatorApi";
import { requestRefresh, setDashboardLoading } from "../../externalApi/DashboardHostApi";
import { getGeoLocationCode, getUserGeoName } from "../../externalApi/DashboardHostApiInternal";
import { getHostInstanceManagerApi } from "../../externalApi/InstanceManagerApi";
import { getSystemTheme } from "../../externalApi/ThemeApi";
import { isFeatureEnabled } from "../../externalApi/VelocityApi";
import { lazyLoadModule } from "../../lazyLoadModule";
import widgetApiVersion from "../apis/api.interfaces";
import { WidgetAuthenticator } from "../hostAccess/hostAccess";
import { PlaceholderImageElement } from "../placeholderImageElement/img-placeholder";
import { getWidgetRegistrarAsync } from "../registrar/registrar";
import { WidgetInnerFocus } from "../widgetApis/WidgetInnerFocus";
import { WidgetInteractionsApi } from "../widgetApis/WidgetInteractionsApi";
import { WidgetLifecycleApi } from "../widgetApis/WidgetLifecycleApi";
import { WidgetPreviewApi } from "../widgetApis/WidgetPreviewApi";
import { WidgetSettingsApi } from "../widgetApis/WidgetSettingsApi";
import { WidgetSizeApi } from "../widgetApis/WidgetSizeApi";
import { WidgetStatusApi } from "../widgetApis/WidgetStatusApi";
import { WidgetVisibilityApi } from "../widgetApis/WidgetVisibilityApi";
import { WidgetInstanceElement } from "../widgetInstanceElement/widget-instance";
import { getWidgetInstanceManager } from "../widgetInstanceManager/widgetInstanceManager";
import { WidgetProperties } from "../widgetInstanceManager/widgetPropertiesInternal";
import { getErrorWidgetLifecycleObserver, getLoadingWidgetLifecycleObserver } from "../widgetLifecycleObserver/widgetLifecycleObserver";
import * as Constants from "../../constants";
const TelemetryModule = import(
/* webpackChunkName: "Telemetry-Chunk" */
"../../../telemetry/Telemetry");
export class WidgetFiltering {
    isRegistrationValid(widgetRegistration, geoName, accountType, anyMSAAvailable, anyAADAvailable, isUserSignedOut) {
        return (this.isRegistrationValidForRegion(widgetRegistration, geoName) &&
            this.isRegistrationValidForAccountType(widgetRegistration, accountType, anyMSAAvailable, anyAADAvailable, isUserSignedOut));
    }
    isRegistrationValidForRegion(registration, region) {
        // If exclusive regions exist(regions the user must be in for the widget to be available), return true if the users selected region is present in the list.
        // else if excluded regions exist and the users selected region is present in the list, return false.
        // else return true that it is valid for all regions
        if (registration.excludedRegionsByLocation &&
            registration.excludedRegionsByLocation.find((excludedRegion) => getGeoLocationCode() === excludedRegion) !==
                undefined) {
            return false;
        }
        else if (registration.excludedRegions &&
            registration.excludedRegions.find((excludedRegion) => region.toLowerCase() === excludedRegion.toLowerCase()) !== undefined) {
            return false;
        } // Consult geoLocationCode to ensure that in China we are not displaying widgets which are unsupported
        else if (registration.excludedRegions && registration.excludedRegions.includes("CN") && getGeoLocationCode() === "CN") {
            return false;
        }
        else if (registration.exclusiveRegions) {
            // For CN compliance, checking the region is not sufficient. We must consult the geoLocationCode which includes consideration for a conservative interpretation of local device state + reverse IP lookup.
            if (!registration.exclusiveRegions.includes("CN") && getGeoLocationCode() === "CN") {
                return false;
            }
            return (registration.exclusiveRegions.find((exclusiveRegion) => region.toLowerCase() === exclusiveRegion.toLowerCase()) !==
                undefined);
        }
        return true;
    }
    isRegistrationValidForAccountType(registration, accountType, anyMSAAvailable, anyAADAvailable, isUserSignedOut) {
        const widgetType = registration.entryPoint.className != "peregrine-widgets";
        // If a supported auth list is provided, return true if the specified auth is in that list.
        if (registration.supportedAuth) {
            if (isUserSignedOut) {
                // If user is signed out, return true only if "none" account type is supported.
                // User may have a valid default account and simply chose to sign out from Widgets, so don't check for MSA or AAD.
                return registration.supportedAuth.find((auth) => auth.toLowerCase() === "none") != undefined;
            }
            const defaultAccountValid = registration.supportedAuth.find((auth) => auth.toLowerCase() === accountType.toLowerCase()) !== undefined;
            // Looking for multi accounts in non peregrine widgets (X-Box, Outlook, AC) if the first account is not valid
            if (widgetType && !defaultAccountValid) {
                return ((anyMSAAvailable === true && registration.supportedAuth.find((auth) => auth.toLowerCase() === "msa") != undefined) ||
                    (anyAADAvailable === true && registration.supportedAuth.find((auth) => auth.toLowerCase() === "aad") != undefined));
            }
            // WebXT handles their own account selection
            return defaultAccountValid;
        }
        return true;
    }
}
// Helper function used to dynamically replace variables in URL with their values
export async function fillURLVariables(queryParams, url, widgetInstance) {
    // Enumerate all values that are present in the additional args, find and replace each in the url.
    // Values in the search params are constructed by the host which may include values from EndpointAdditionalArguments
    // Values in the registry should be URI encoded.
    const urlParams = new URLSearchParams(queryParams);
    urlParams.forEach((value, key) => {
        const token = "$(" + key + ")";
        // Values in urlParams are already pre-decoded so an additional decodeURI call is not necessary.
        if (url.includes(token)) {
            let errorValue = undefined;
            try {
                url = url.split(token).join(value);
            }
            catch (error) {
                errorValue = error;
            }
            Trace.logEvent(dashboard, "FillUrlVariables", 0 /* verbose */, {
                error: errorValue ? errorValue : "",
                token: token,
            });
        }
    });
    // Special case the UPN token and replace it with the UPN of the default account
    if (url.includes("$(UPN)")) {
        const authenticator = widgetInstance.tryGetApi("authenticator", widgetApiVersion, widgetApiVersion);
        if (authenticator) {
            try {
                let upn = undefined;
                let isSignedIn = false;
                if (isFeatureEnabled('Feature_AuthApiV5')) {
                    const authV5Api = await getHostAuthenticatorApiV5();
                    if (authV5Api) {
                        const accountId = await authV5Api.getSignedInAccount("");
                        if (accountId) {
                            isSignedIn = true;
                            upn = await authV5Api.getAccountProperty(accountId, "UPN");
                        }
                    }
                }
                else {
                    isSignedIn = true;
                    upn = await authenticator.getProperty("UPN");
                }
                if (!upn || upn.length === 0) {
                    if (isFeatureEnabled('Feature_AuthApiV5')) {
                        if (isSignedIn) {
                            widgetInstance.widgetState = "error";
                        }
                    }
                    throw new Error("EmptyUPNStringReceived");
                }
                // We cannot use replaceAll because it is only available in es2021
                url = url.split("$(UPN)").join(upn);
            }
            catch (error) {
                // Log the failure and remove UPN from the URL
                Trace.logEvent(dashboard, "FillUPNFailed", 0 /* verbose */, {
                    reason: error,
                });
                url = url.split("$(UPN)/").join("");
            }
        }
    }
    // Remove all non matched tokens of the form $(.*)
    try {
        url = url.replace(/\$\([^\)]*\)/g, "");
    }
    catch (error) {
        Trace.logEvent(dashboard, "RemoveNonMatchedTokens", 0 /* verbose */, {
            reason: error,
            url: url,
        });
    }
    return url;
}
export function getEntryPointOverride(registration, key) {
    if (registration.entryPointOverrides !== undefined) {
        for (let i = 0; i < registration.entryPointOverrides.length; ++i) {
            if (registration.entryPointOverrides[i].key.toUpperCase() === key.toUpperCase()) {
                return registration.entryPointOverrides[i];
            }
        }
    }
    return null;
}
export function areWidgetInstancesInDefaultState(widgetInstances, numDefaultWidgets) {
    var _a, _b, _c;
    if (widgetInstances.length != numDefaultWidgets) {
        return false;
    }
    for (let i = 0; i < widgetInstances.length; ++i) {
        if (!widgetInstances[i].widgetRegistration.isDefault ||
            !widgetInstances[i].widgetRegistration.defaults ||
            widgetInstances[i].getWidgetProperty("column") != ((_a = widgetInstances[i].widgetRegistration.defaults) === null || _a === void 0 ? void 0 : _a.column) ||
            widgetInstances[i].getWidgetProperty("position") != ((_b = widgetInstances[i].widgetRegistration.defaults) === null || _b === void 0 ? void 0 : _b.position) ||
            widgetInstances[i].getWidgetProperty("size") != ((_c = widgetInstances[i].widgetRegistration.defaults) === null || _c === void 0 ? void 0 : _c.defaultSize)) {
            return false;
        }
    }
    return true;
}
export class WidgetOrchestratorImpl {
    constructor(widgetInstanceManager, widgetRegistrar) {
        Trace.logEvent(dashboard, "WebAppOrchestratorCreated", 0 /* verbose */);
        if (!customElements.get("widget-instance")) {
            customElements.define("widget-instance", WidgetInstanceElement);
        }
        if (!customElements.get("img-placeholder")) {
            customElements.define("img-placeholder", PlaceholderImageElement, { extends: "img" });
        }
        this._eventListeners = new Array();
        this._peregrineLoadListeners = new Map();
        this._peregrineLoadTimestamp = 0;
        this._widgetHtmlElements = new Map();
        this._widgetInstanceManager = widgetInstanceManager;
        this._widgetRegistrar = widgetRegistrar;
        // Initialize the time given to widgets to refresh their content, allowing for
        // an override value to be used if it was specified in the URL parameters.
        this._widgetContentRefreshTimeSeconds = 10;
        const urlParams = new URLSearchParams(window.location.search);
        const contentRefreshTimeParam = urlParams.get("contentrefreshtime");
        if (contentRefreshTimeParam !== null) {
            this._widgetContentRefreshTimeSeconds = parseInt(contentRefreshTimeParam, 10);
        }
        // Start the process that will load/initialize initially present Peregrine widgets
        // once their containers are present on the page.
        this.startInitialPeregrineWidgetLoad();
        // This event is fired from peregrine side when PCS failed to load:
        // https://dev.azure.com/msasg/ContentServices/_git/msnews-experiences?path=/libs/widget-initializer/src/WidgetInitializer.ts&version=GBmain&line=114&lineEnd=115&lineStartColumn=1&lineEndColumn=1&lineStyle=plain&_a=contents.
        if (!WidgetOrchestratorImpl._listenderAddedForPCSFailure) {
            window.addEventListener("CustomWidgetErrorEvent", (e) => {
                if (e && e.detail && e.detail.payload && e.detail.payload.stage === "initialize") {
                    // Only handle this event fired when initializing PCS
                    this.sendAllPeregrineWidgetsToErrorState("PeregrinePCSLoadFailure");
                }
            });
        }
        WidgetOrchestratorImpl._listenderAddedForPCSFailure = true;
    }
    async getWidgetRegistrations() {
        var e_1, _a;
        const widgetFiltering = new WidgetFiltering();
        const geoName = getUserGeoName();
        let accountType = "";
        if (isFeatureEnabled("Feature_AuthApiV5")) {
            const authenticator = await getHostAuthenticatorApiV5();
            if (authenticator) {
                // If we're signed in, get the provider authority of the signed in account. Otherwise use the default account for signed out/unknown.
                const signinStatus = await authenticator.getSignInStatus();
                let accountId = "";
                if (signinStatus === 1 /* SignedIn */) {
                    accountId = await authenticator.getSignInTargetAccountId();
                }
                else {
                    accountId = await authenticator.getDefaultAccountId();
                }
                const provider = accountId ? await authenticator.getAccountProperty(accountId, "ProviderAuthority") : "unknown";
                accountType = provider === "organizations" ? "AAD" : "MSA";
            }
        }
        else {
            accountType = await getDefaultAccountType();
        }
        const authenticator = window.__com_microsoft_dsh_widgetModelApiFactory.tryGetApi("authenticator", 1, 1);
        let anyMSAAvailable = false;
        let anyAADAvailable = false;
        let isUserSignedOut = false;
        if (authenticator) {
            const accountIds = await authenticator.getAccountIdsAsync();
            try {
                for (var accountIds_1 = __asyncValues(accountIds), accountIds_1_1; accountIds_1_1 = await accountIds_1.next(), !accountIds_1_1.done;) {
                    const accountId = accountIds_1_1.value;
                    if (await authenticator.isMSAAccountAsync(accountId))
                        anyMSAAvailable = true;
                    else if (await authenticator.isAADAccountAsync(accountId))
                        anyAADAvailable = true;
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (accountIds_1_1 && !accountIds_1_1.done && (_a = accountIds_1.return)) await _a.call(accountIds_1);
                }
                finally { if (e_1) throw e_1.error; }
            }
            isUserSignedOut = !(await authenticator.isUserSignedInAsync()) || (await authenticator.getDefaultAccountIdAsync()) === "";
        }
        // Return filtered registrations by region and default account type
        let filteredRegistrations = (await this._widgetRegistrar.getWidgetRegistrations()).filter((widgetRegistration) => {
            return widgetFiltering.isRegistrationValid(widgetRegistration, geoName, accountType, anyMSAAvailable, anyAADAvailable, isUserSignedOut);
        });
        return filteredRegistrations;
    }
    async getRiverInstances() {
        return this._widgetInstanceManager.getRiverWidgetInstances();
    }
    async getWidgetInstances() {
        const widgetFiltering = new WidgetFiltering();
        const geoName = getUserGeoName();
        const accountType = await getDefaultAccountType();
        // Filter widgets stored by the current user geo region and default account type
        let filteredInstances = (await this._widgetInstanceManager.getWidgetInstances()).filter((widgetInstance) => widgetFiltering.isRegistrationValid(widgetInstance.widgetRegistration, geoName, accountType, undefined, undefined, undefined));
        let isRemoveDefaultInstancesEnabled = isFeatureEnabled("Feature_RemoveDefaultInstances");
        if (isRemoveDefaultInstancesEnabled) {
            let defaultStateCheckCompleted = localStorage.getItem("DefaultStateCheckCompleted");
            // getWidgetInstances is called a LOT. Avoid logging telemetry for something we don't necessarily need to know
            Trace.logEvent(dashboard, "CheckDefaultStateForDynamicFeed", 0 /* verbose */, {
                defaultStateCheckCompleted: defaultStateCheckCompleted,
            });
            if (!defaultStateCheckCompleted || defaultStateCheckCompleted === "false") {
                localStorage.setItem("DefaultStateCheckCompleted", "true");
                // match filtering of widget instances by user geo region and default account type
                let filteredRegistrations = (await this._widgetRegistrar.getWidgetRegistrations()).filter((widgetRegistration) => widgetRegistration.isDefault == true &&
                    widgetFiltering.isRegistrationValid(widgetRegistration, geoName, accountType, undefined, undefined, undefined));
                let shouldDefaultWidgetsBeRemoved = areWidgetInstancesInDefaultState(filteredInstances, filteredRegistrations.length);
                if (shouldDefaultWidgetsBeRemoved) {
                    lazyLoadModule(TelemetryModule).then((Telemetry) => {
                        Telemetry.logEvent(dashboard, "RemoveDefaultWidgetInstances", 16777216 /* productAndServicePerformance */);
                    });
                    let numRemovedWidgets = 0;
                    filteredInstances.forEach((i) => {
                        numRemovedWidgets++;
                        this.removeWidgetInstance(i);
                    });
                    Measures.logEvent(dashboard, "StateCheckForRemoveDefaultInstances", 16777216 /* productAndServicePerformance */, {
                        numMatchingRegistrations: filteredRegistrations.length,
                        numRemovedWidgets: numRemovedWidgets,
                    });
                    return new Array();
                }
            }
        }
        return filteredInstances;
    }
    async getFeedInstance() {
        return this._widgetInstanceManager.getFeedInstance();
    }
    async getWidget(widgetInstance) {
        if (this._widgetHtmlElements.has(widgetInstance)) {
            return this._widgetHtmlElements.get(widgetInstance);
        }
        else {
            return this.initializeWidget(widgetInstance);
        }
    }
    async addWidgetInstance(widgetRegistration, widgetId) {
        Trace.logEvent(dashboard, "WebAppOrchestratorAddingWidgetInstance", 0 /* verbose */, {
            appId: widgetRegistration.appId,
            providerId: widgetRegistration.providerId,
            widgetDefinitionId: widgetRegistration.id,
        });
        try {
            const widget = await this._widgetInstanceManager.addWidgetInstanceForPin(widgetRegistration, widgetId);
            this.fireEvent("widgetAdded", widget);
            return widget;
        }
        catch (error) {
            throw error;
        }
    }
    async addWidgetInstanceForRiver(widgetRegistration) {
        lazyLoadModule(TelemetryModule).then((Telemetry) => {
            Telemetry.logEvent(dashboard, "WebAppOrchestratorAddingWidgetInstanceForRiver", 16777216 /* productAndServicePerformance */, {
                appId: widgetRegistration.appId,
                providerId: widgetRegistration.providerId,
                widgetDefinitionId: widgetRegistration.id,
            });
        });
        try {
            return this._widgetInstanceManager.addWidgetInstanceForRiver(widgetRegistration);
        }
        catch (error) {
            throw error;
        }
    }
    removeWidgetInstance(widgetInstance) {
        lazyLoadModule(TelemetryModule).then((Telemetry) => {
            Telemetry.logEvent(dashboard, "WebAppOrchestratorRemovingWidgetInstance", 16777216 /* productAndServicePerformance */, {
                appId: widgetInstance.widgetRegistration.appId,
                providerId: widgetInstance.widgetRegistration.providerId,
                widgetDefinitionId: widgetInstance.widgetDefinitionId,
                widgetInstanceId: widgetInstance.widgetId,
            });
        });
        this._widgetInstanceManager
            .removeWidgetInstanceFromPins(widgetInstance)
            .then((widgetInstance) => {
            this._widgetHtmlElements.delete(widgetInstance);
            this.fireEvent("widgetRemoved", widgetInstance);
        })
            .catch((error) => {
            throw error;
        });
    }
    isRiverInstance(widgetInstance) {
        return this._widgetInstanceManager.isRiverInstance(widgetInstance);
    }
    removeWidgetInstanceFromRiver(widgetInstance, isHide) {
        lazyLoadModule(TelemetryModule).then((Telemetry) => {
            Telemetry.logEvent(dashboard, "WebAppOrchestratorRemovingWidgetInstanceFromRiver", 16777216 /* productAndServicePerformance */, {
                appId: widgetInstance.widgetRegistration.appId,
                isHide: isHide,
                providerId: widgetInstance.widgetRegistration.providerId,
                widgetDefinitionId: widgetInstance.widgetDefinitionId,
                widgetInstanceId: widgetInstance.widgetId,
            });
        });
        try {
            if (this.isRiverInstance(widgetInstance)) {
                this._widgetInstanceManager.removeWidgetInstanceFromRiver(widgetInstance);
                this._widgetHtmlElements.delete(widgetInstance);
                // Only signal hide if this came from user intent.
                if (isHide) {
                    this.fireEvent("widgetHidden", widgetInstance);
                }
            }
        }
        catch (error) {
            throw error;
        }
    }
    addEventListener(listener) {
        this._eventListeners.push(listener);
    }
    removeEventListener(listener) {
        let index = this._eventListeners.indexOf(listener);
        if (index > -1) {
            this._eventListeners.splice(index, 1);
        }
    }
    async updateWidgetInstance(widgetInstance) {
        Trace.logEvent(dashboard, "WebAppOrchestratorUpdatingWidgetInstance", 0 /* verbose */, {
            appId: widgetInstance.widgetRegistration.appId,
            providerId: widgetInstance.widgetRegistration.providerId,
            widgetDefinitionId: widgetInstance.widgetDefinitionId,
            widgetInstanceId: widgetInstance.widgetId,
        });
        return this._widgetInstanceManager.updateWidgetInstance(widgetInstance);
    }
    async sendAllPeregrineWidgetsToErrorState(widgetErrorStateReason) {
        Measures.logEvent(dashboard, "WebAppOrchestratorSendingAllPeregrineWidgetsToError", 16777216 /* productAndServicePerformance */);
        (await this.getAllWidgetInstances()).map((widgetInstance) => {
            if (typeof widgetInstance.widgetRegistration.entryPoint.host === "undefined" && widgetInstance.widgetState !== "error") {
                widgetInstance.widgetErrorStateReason = widgetErrorStateReason;
                widgetInstance.widgetErrorCode = 0x8000ffff; // E_UNEXPECTED
                widgetInstance.widgetState = "error";
            }
        });
    }
    async peregrineWidgetStatusUpdated(widget, oldVal, newVal) {
        var _a, _b, _c, _d, _e, _f, _g;
        // Can be error, loading, loaded, normal. Set error state and widget load perf when these attributes change.
        if (newVal === "error") {
            for (const [instance, _] of this._widgetHtmlElements) {
                if (((_a = widget.instance) === null || _a === void 0 ? void 0 : _a.instanceId) == instance.instanceId && instance.widgetState !== "error") {
                    instance.widgetErrorStateReason = "PeregrineLoadFailure";
                    instance.widgetErrorCode = 0x8000ffff; // E_UNEXPECTED
                    instance.widgetState = "error";
                }
            }
            this.removeAttributeListener(widget);
        }
        else if (newVal === "loaded") {
            let now = new Date().getTime();
            Trace.logEvent(dashboard, "WebAppWidgetLoadedTime", 0 /* verbose */, {
                indexedDBInstanceId: (_b = widget.instance) === null || _b === void 0 ? void 0 : _b.instanceId,
                loadTime: now - this._peregrineLoadTimestamp,
                widgetDefinitionId: (_c = widget.instance) === null || _c === void 0 ? void 0 : _c.widgetDefinitionId,
                widgetInstanceId: (_d = widget.instance) === null || _d === void 0 ? void 0 : _d.widgetId,
            });
        }
        else if (newVal === "normal") {
            let now = new Date().getTime();
            Trace.logEvent(dashboard, "WebAppWidgetNormalTime", 0 /* verbose */, {
                indexedDBInstanceId: (_e = widget.instance) === null || _e === void 0 ? void 0 : _e.instanceId,
                loadTime: now - this._peregrineLoadTimestamp,
                widgetDefinitionId: (_f = widget.instance) === null || _f === void 0 ? void 0 : _f.widgetDefinitionId,
                widgetInstanceId: (_g = widget.instance) === null || _g === void 0 ? void 0 : _g.widgetId,
            });
            this.removeAttributeListener(widget);
        }
    }
    listenForPeregrineStatus() {
        // Bind callback to this so we can access this._peregrineLoadTimestamp later.
        let callback = ((widget, oldVal, newVal) => {
            this.peregrineWidgetStatusUpdated(widget, oldVal, newVal);
        }).bind(this);
        for (const [instance, element] of this._widgetHtmlElements) {
            element.then((value) => {
                this._peregrineLoadListeners.set(instance.instanceId, value.addAttributeListener("data-widget-state", callback));
            });
        }
    }
    loadPeregrineWidget(widgetInstance) {
        Trace.logEvent(dashboard, "WebAppOrchestratorLoadingPeregrineWidget", 0 /* verbose */, {
            appId: widgetInstance.widgetRegistration.appId,
            providerId: widgetInstance.widgetRegistration.providerId,
            widgetDefinitionId: widgetInstance.widgetDefinitionId,
            widgetInstanceId: widgetInstance.widgetId,
        });
        if (widgetInstance) {
            if (window.widget__initWidgets) {
                Trace.logEvent(dashboard, "WebAppOrchestratorInitPeregrineWidget", 0 /* verbose */, {
                    appId: widgetInstance.widgetRegistration.appId,
                    providerId: widgetInstance.widgetRegistration.providerId,
                    widgetDefinitionId: widgetInstance.widgetDefinitionId,
                    widgetInstanceId: widgetInstance.widgetId,
                });
                window.widget__initWidgets([
                    widgetInstance.widgetId + ":" + widgetInstance.instanceId,
                ] /* optional: widgetInstance.getWidgetProperty("sessionId") */);
            }
            else {
                this.loadPeregrineWidgets();
            }
        }
    }
    loadSpecificPeregrineWidgets(widgetInstances) {
        let widgetIdList = new Array();
        for (let widgetInstance of widgetInstances) {
            widgetIdList.push(widgetInstance.widgetId + ":" + widgetInstance.instanceId);
        }
        Trace.logEvent(dashboard, "WebAppOrchestratorLoadingSpecificPeregrineWidgets", 0 /* verbose */, {
            widgetIds: widgetIdList,
        });
        if (widgetInstances.length > 0) {
            if (window.widget__initWidgets) {
                Trace.logEvent(dashboard, "WebAppOrchestratorInitSpecificPeregrineWidgets", 0 /* verbose */, {
                    widgetIds: widgetIdList,
                });
                window.widget__initWidgets(widgetIdList);
            }
            else {
                this.loadPeregrineWidgets();
            }
        }
    }
    isUserRegionChina() {
        return getGeoLocationCode() === "CN";
    }
    isUserRegionUnitedStates() {
        return getGeoLocationCode() === "US";
    }
    async loadPeregrineWidgets() {
        Trace.logEvent(dashboard, "WebAppOrchestratorLoadingPeregrineWidgets", 0 /* verbose */);
        this._peregrineLoadTimestamp = new Date().getTime();
        let isPcsCallSuccess = true;
        let widgetInitializer = await fetchWidgetIntializer().catch((e) => {
            Measures.logEvent(dashboard, "WebAppOrchestratorPcsInitImport", 16777216 /* productAndServicePerformance */, {
                error: e,
                success: false,
            });
            isPcsCallSuccess = false;
            this.sendAllPeregrineWidgetsToErrorState("PeregrineLoadFailure");
        });
        if (isPcsCallSuccess) {
            Measures.logEvent(dashboard, "WebAppOrchestratorPcsInitImport", 16777216 /* productAndServicePerformance */, {
                error: "",
                success: true,
            });
            // TODO: Awaiting implementation from WebXT http://osgvsowi/40547543
            addMarkerToPageTimings("MEE.WidgetInitializerStart");
            widgetInitializer.startWidgetInitializer().then((value) => {
                addMarkerToPageTimings("MEE.WidgetInitializerEnd");
                lazyLoadModule(TelemetryModule).then((Telemetry) => {
                    Telemetry.logEvent(dashboard, "WebAppPerfMeasure", 16777216 /* productAndServicePerformance */, {
                        type: "StartWidgetInitializerEnd"
                    });
                });
                let buildInfo = value;
                if (buildInfo) {
                    TelemetryData.pcsBuildVersion = value === null || value === void 0 ? void 0 : value.pcs;
                    TelemetryData.widgetsBuildVersion = value === null || value === void 0 ? void 0 : value.client;
                }
            });
        }
    }
    async reloadFailedWidgets(lifecycle, forceReloadPage) {
        // Peregrine
        let pgWidgets = (await this.getAllWidgetInstances()).filter((widgetInstance) => typeof widgetInstance.widgetRegistration.entryPoint.host === "undefined" && widgetInstance.widgetState === "error");
        for (let pgWidget of pgWidgets) {
            pgWidget.widgetState = "loading";
        }
        const coreBundleScripts = document.body.getElementsByClassName("peregrine-core-bundle");
        if (coreBundleScripts.length === 0 || forceReloadPage) {
            Measures.logEvent(dashboard, "WebAppOrchestratorFullPageReload", 16777216 /* productAndServicePerformance */, {
                peregrineWidgets: pgWidgets,
            });
            // We need to set the dashboard loading state to false to reload the page
            await setDashboardLoading(false);
            // PCS failed, we reload the whole page
            await requestRefresh();
        }
        else if (pgWidgets.length > 1) {
            this.loadSpecificPeregrineWidgets(pgWidgets);
        }
        else if (pgWidgets.length === 1) {
            this.loadPeregrineWidget(pgWidgets[0]);
        }
        // Non-Peregrine
        this.reloadFailedNonPeregrineWidgets(lifecycle, await this.getAllWidgetInstances());
        // Non-Peregrine in the river
        this.reloadFailedNonPeregrineWidgets(lifecycle, this._widgetInstanceManager.getRiverWidgetInstances());
    }
    // To reload dynamic feed FAST widget using passed in widgetInstance.
    async reloadFailedWidgetsInDynamicFeed(widgetInstance) {
        this.getWidget(widgetInstance).then(async (widgetElement) => {
            if (widgetInstance.widgetState === "error" && widgetElement.initializeFn) {
                await widgetElement.initializeFn();
                // Because this was a user action clicking the reload button and the widget is already visible, activate immediately
                // passing true that this is the first activation to force notification of any current payload.
                let instanceManagerApi = await getHostInstanceManagerApi();
                await (instanceManagerApi === null || instanceManagerApi === void 0 ? void 0 : instanceManagerApi.activateWidget(widgetInstance.widgetId, true));
            }
        });
    }
    async refreshWidgets() {
        Trace.logEvent(dashboard, "WebAppOrchestratorRefreshingWidgets", 0 /* verbose */);
        let isPcsCallSuccess = true;
        let peregrineChangesPromise = new Promise(async (resolve) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            // Call PCS API to check whether newer version of bundles are available
            let widgetInitializer = await fetchWidgetIntializer().catch((e) => {
                // Let widgets stay in current state.
                // Widgets shouldn't go to error state when PCS doesn't clearly
                // indicate a refresh is needed.
                isPcsCallSuccess = false;
                Trace.logEvent(dashboard, "WebAppOrchestratorPcsRefreshImportFailed", 0 /* verbose */);
            });
            let shouldRefresh = false;
            if (isPcsCallSuccess) {
                let isPeregrineChangesSuccess = true;
                let peregrineChanges = await widgetInitializer.widgetsChanged().catch(() => {
                    // Let widgets stay in current state.
                    // Widgets shouldn't go to error state when PCS doesn't clearly
                    // indicate a refresh is needed.
                    isPeregrineChangesSuccess = false;
                    Trace.logEvent(dashboard, "WebAppOrchestratorRefreshingWidgetsPcsWidgetsChangedFailed", 0 /* verbose */);
                });
                if (isPeregrineChangesSuccess) {
                    TelemetryData.pcsBuildVersion = (_b = (_a = peregrineChanges.changeReason) === null || _a === void 0 ? void 0 : _a.buildVersion) === null || _b === void 0 ? void 0 : _b.pcs;
                    TelemetryData.widgetsBuildVersion = (_d = (_c = peregrineChanges.changeReason) === null || _c === void 0 ? void 0 : _c.buildVersion) === null || _d === void 0 ? void 0 : _d.client;
                    if (peregrineChanges.widgetsChangedInfoVersion >= 1) {
                        // Be conservative with refreshing more frequently than planned to limit load on services
                        Measures.logEvent(dashboard, "WebAppOrchestratorPcsRefreshVersionValid", 16777216 /* productAndServicePerformance */, {
                            anyChange: peregrineChanges.anyChange,
                            isSuccess: peregrineChanges.isSuccess,
                            version: peregrineChanges.widgetsChangedInfoVersion,
                            changeReasonFlight: (_e = peregrineChanges.changeReason) === null || _e === void 0 ? void 0 : _e.flightsChanged,
                            changeReasonBundleHash: (_f = peregrineChanges.changeReason) === null || _f === void 0 ? void 0 : _f.bundleHashChanged,
                            changeReasonConfigsHash: (_g = peregrineChanges.changeReason) === null || _g === void 0 ? void 0 : _g.configsHashChanged,
                            changeReasonLocConfigsHash: (_h = peregrineChanges.changeReason) === null || _h === void 0 ? void 0 : _h.locConfigsHashChanged,
                        });
                        shouldRefresh = peregrineChanges.isSuccess && (peregrineChanges === null || peregrineChanges === void 0 ? void 0 : peregrineChanges.anyChange);
                    }
                    else {
                        Measures.logEvent(dashboard, "WebAppOrchestratorPcsRefreshVersionInvalid", 16777216 /* productAndServicePerformance */, {
                            anyChange: peregrineChanges.anyChange,
                            isSuccess: peregrineChanges.isSuccess,
                            version: peregrineChanges.widgetsChangedInfoVersion,
                        });
                        // If unsupported, then wait until next opportunity to check
                        shouldRefresh = false;
                    }
                }
            }
            resolve(shouldRefresh);
        });
        // Since the full page (including all rendered widgets) are rendered by peregrine, first
        // check to see if a page refresh is needed before refreshing individual widgets. If it is,
        // refreshing individual widgets is not needed since the full page will refresh anyway.
        let peregrineChangesResult = await peregrineChangesPromise;
        if (peregrineChangesResult) {
            return true;
        }
        // Give widgets a chance to refresh their content.
        (await this.getAllWidgetInstances()).map((widgetInstance) => {
            // Platform widgets will push updates, we should not attempt to pull data before it is ready
            if (!widgetInstance.isPlatform) {
                return widgetInstance.refreshContent();
            }
            return;
        });
        // If any widgets are in an error state, reload them
        await this.reloadFailedWidgets("contentrefresh");
        // For now, always wait until the full refresh timeout period has passed before completing the refresh. In the future,
        // this should be updated to wait only until all widgets have indicated they are done refreshing.
        const refreshTimeoutPromise = new Promise((resolve) => setTimeout(resolve, 1000 * this._widgetContentRefreshTimeSeconds));
        await refreshTimeoutPromise;
        Trace.logEvent(dashboard, "WebAppOrchestratorWidgetRefreshComplete", 0 /* verbose */);
        return false;
    }
    async suspendWidgets() {
        Trace.logEvent(dashboard, "WebAppOrchestratorSuspendingWidgets", 0 /* verbose */);
        // Suspend any active widgets.
        const suspendPromises = (await this.getAllWidgetInstances()).map((widgetInstance) => {
            return widgetInstance.suspend();
        });
        // Wait for all active widgets to suspend.
        await Promise.all(suspendPromises);
        Trace.logEvent(dashboard, "WebAppOrchestratorWidgetSuspendComplete", 0 /* verbose */);
    }
    async resumeWidgets() {
        Trace.logEvent(dashboard, "WebAppOrchestratorResumingWidgets", 0 /* verbose */);
        // Resume any active widgets.
        await Promise.all((await this.getAllWidgetInstances()).map(async (widgetInstance) => {
            await widgetInstance.resume();
        }));
        // If any widgets are in an error state, reload them
        await this.reloadFailedWidgets("resumed");
        Measures.logEvent(dashboard, "WebAppOrchestratorWidgetResumeComplete", 16777216 /* productAndServicePerformance */);
    }
    widgetInnerFocus(widgetInstance) {
        this.fireEvent("widgetInnerFocus", widgetInstance);
    }
    async initializeOutlookWidgetAsync(widgetInstance, widgetInstanceElement, entryPointHost) {
        var _a, _b, _c, _d;
        Measures.logEvent(dashboard, "InitializeOutlookWidget", 16777216 /* productAndServicePerformance */, {
            appId: widgetInstance.widgetRegistration.appId,
            isTombstoned: widgetInstance.widgetRegistration.isTombstoned,
            providerId: widgetInstance.widgetRegistration.providerId,
            widgetDefinitionId: widgetInstance.widgetDefinitionId,
            widgetInstanceId: widgetInstance.widgetId,
        });
        log.info("orchestrator.initializeWidgetFn [" + widgetInstance.widgetId + "] entry point: " + entryPointHost);
        try {
            // When an Outlook widget is loaded, do the following:
            // (1) Import its JS endpoint as a module
            // (2) Append a new child to widgetInstanceElement with a tag of the format "widget-id"
            // (3) Call into the required widget export "defineWidget" with this tag name. The widget is then expected to assign its own component to this tag using define Opens in new window or tab
            // (OR) Render Outlook Widget with Adaptive Card Tombstone UI when Edge is uninstalled
            // (OR) Render Outlook Widget with Adaptive Card Signout UI when user is signed out
            // (4) Place widget into error state if this process fails
            const src = await fillURLVariables(window.location.search, entryPointHost, widgetInstance);
            // webpackIgnore prevents Webpack from trying to import the url at build time(which would cause a failure to reach the endpoint at runtime)
            const widgetModule = (await import(/* webpackIgnore: true */ src));
            // Tag names must be separated by a dash and be unique to the given instance
            const dashedName = widgetInstance.widgetId.replace(/\./g, "-") + "-" + widgetInstance.instanceId;
            const exportedTag = document.createElement(dashedName);
            widgetInstanceElement.appendChild(exportedTag);
            // Do not render Outlook widgets when tombstoned
            if (!widgetInstance.widgetRegistration.isTombstoned) {
                // OWA cannot export defineWidget from their module so we must check their global for the function
                if (widgetInstance.widgetId === Constants.CalendarWidgetId && ((_b = (_a = window === null || window === void 0 ? void 0 : window.Owa) === null || _a === void 0 ? void 0 : _a.calendarwidget) === null || _b === void 0 ? void 0 : _b.defineWidget)) {
                    if (!widgetInstance.isCustomElementDefined) {
                        window.Owa.calendarwidget.defineWidget({
                            elementName: dashedName,
                            src: src,
                        });
                        widgetInstance.isCustomElementDefined = true;
                    }
                }
                else if (widgetInstance.widgetId === Constants.ToDoWidgetId && ((_d = (_c = window === null || window === void 0 ? void 0 : window.Owa) === null || _c === void 0 ? void 0 : _c.todowidget) === null || _d === void 0 ? void 0 : _d.defineWidget)) {
                    if (!widgetInstance.isCustomElementDefined) {
                        window.Owa.todowidget.defineWidget({
                            elementName: dashedName,
                            src: src,
                        });
                        widgetInstance.isCustomElementDefined = true;
                    }
                }
                else if (widgetModule.defineWidget) {
                    if (!widgetInstance.isCustomElementDefined) {
                        widgetModule.defineWidget({
                            elementName: dashedName,
                            src: src,
                        });
                        widgetInstance.isCustomElementDefined = true;
                    }
                }
            }
            Measures.logEvent(dashboard, "InitializeOutlookWidgetComplete", 16777216 /* productAndServicePerformance */, {
                appId: widgetInstance.widgetRegistration.appId,
                defineWidgetExported: widgetModule === null || widgetModule === void 0 ? void 0 : widgetModule.defineWidget,
                isCustomElementDefined: widgetInstance.isCustomElementDefined,
                isTombstoned: widgetInstance.widgetRegistration.isTombstoned,
                providerId: widgetInstance.widgetRegistration.providerId,
                widgetDefinitionId: widgetInstance.widgetDefinitionId,
                widgetInstanceId: widgetInstance.widgetId,
            });
        }
        catch (error) {
            Measures.logEvent(dashboard, "ModuleCallFailed", 16777216 /* productAndServicePerformance */, {
                failureMessage: error.message,
                failureName: error.name,
                widgetUrl: entryPointHost,
            });
            widgetInstance.widgetErrorStateReason = "ModuleCallFailed";
            widgetInstance.widgetErrorCode = 0x8000ffff; // E_UNEXPECTED
            widgetInstance.widgetState = "error";
        }
    }
    reloadFailedNonPeregrineWidgets(lifecycle, instances) {
        instances
            .filter((widgetInstance) => (typeof widgetInstance.widgetRegistration.entryPoint.host !== "undefined" ||
            widgetInstance.widgetRegistration.isAdaptiveCard) &&
            widgetInstance.widgetState === "error")
            .map((widgetInstance) => {
            this.getWidget(widgetInstance).then(async (widgetElement) => {
                if (widgetInstance.widgetState === "error" && widgetElement.initializeFn) {
                    // If a lifecycle event was specified, this is an attempt to automatically recover
                    if (lifecycle) {
                        Measures.logEvent(dashboard, "AutoReloadWidget", 16777216 /* productAndServicePerformance */, {
                            appId: widgetInstance.widgetRegistration.appId,
                            lifecycleEvent: lifecycle,
                            providerId: widgetInstance.widgetRegistration.providerId,
                            widgetDefinitionId: widgetInstance.widgetDefinitionId,
                            widgetInstanceId: widgetInstance.widgetId,
                        });
                        if (widgetInstance.isPlatform) {
                            // If a platform widget was in an error state, ensure it is activated to give it
                            // an opportunity to recover.  Force the notify if data is available.
                            let instanceManagerApi = await getHostInstanceManagerApi();
                            await (instanceManagerApi === null || instanceManagerApi === void 0 ? void 0 : instanceManagerApi.activateWidget(widgetInstance.widgetId, true));
                        }
                    }
                }
            });
        });
    }
    // // https://assets.msn.com/bundles/v1/winWidgets/latest/widget-initializer.js
    getWidgetInitializerUrl(appType) {
        let widgetInitializerDomain = this.isUserRegionChina() ? "https://assets.msn.cn" : "https://assets.msn.com";
        let widgetInitializerUrl = widgetInitializerDomain + "/bundles/v1/" + appType + "/latest/widget-initializer.js";
        return widgetInitializerUrl;
    }
    // Starts the initial loading/initialization process for Peregrine widgets. Peregrine
    // widgets must be loaded/initialized by the Peregrine PCS system. This must be done
    // once after all Peregrine widget containers have been created on the page.
    async startInitialPeregrineWidgetLoad() {
        addMarkerToPageTimings("MEE.InitialPeregrineWidgetLoadStart");
        // Set up the WidgetLifecycleObserver that will track how many widgets end up
        // in the error state.
        const initialWidgetInstances = await this.getAllWidgetInstances();
        getErrorWidgetLifecycleObserver().setNumWidgets(initialWidgetInstances.length);
        initialWidgetInstances.forEach((widgetInstance) => {
            getErrorWidgetLifecycleObserver().registerWidget(widgetInstance);
        });
        // Set up the WidgetLifeCycleObserver that will track when Peregrine widgets are ready to
        // be loaded/initialized by PCS.
        const initialPeregrineWidgetInstances = initialWidgetInstances.filter((widgetInstance) => {
            return typeof widgetInstance.widgetRegistration.entryPoint.host === "undefined";
        });
        getLoadingWidgetLifecycleObserver().setNumWidgets(initialPeregrineWidgetInstances.length);
        initialPeregrineWidgetInstances.forEach((widgetInstance) => {
            getLoadingWidgetLifecycleObserver().registerWidget(widgetInstance);
        });
    }
    initializeWidget(widgetInstance) {
        Trace.logEvent(dashboard, "WebAppOrchestratorInitializingWidget", 0 /* verbose */, {
            appId: widgetInstance.widgetRegistration.appId,
            providerId: widgetInstance.widgetRegistration.providerId,
            widgetDefinitionId: widgetInstance.widgetDefinitionId,
            widgetInstanceId: widgetInstance.widgetId,
        });
        // Create a <widget-instance> element and attach the widgetInstance object to it
        // so that widgets are able to access the widgetInstance object from the parent of their
        // content root:
        // <widget-instance>
        //      <content-root-element-of-widget/>
        // </widget-instance>
        let widgetInitPromise = new Promise(async (resolve) => {
            // Initialize with core api
            const widgetInstanceElement = document.createElement("widget-instance");
            widgetInstanceElement.instance = widgetInstance;
            widgetInstanceElement.id = widgetInstance.widgetId + ":" + widgetInstance.instanceId;
            let systemColors = await getSystemTheme();
            const systemBackgroundColor = systemColors.substr(0, 6);
            const isDarkTheme = systemBackgroundColor === "000000";
            widgetInstance.setApi("status", 1, new WidgetStatusApi(widgetInstance));
            widgetInstance.setApi("visibility", 1, new WidgetVisibilityApi(widgetInstance));
            widgetInstance.setApi("settings", 1, new WidgetSettingsApi(widgetInstance));
            widgetInstance.setApi("sizes", 1, new WidgetSizeApi(widgetInstance));
            widgetInstance.setApi("lifecycle", 1, new WidgetLifecycleApi(widgetInstance));
            widgetInstance.setApi("authenticator", 1, new WidgetAuthenticator(widgetInstance));
            widgetInstance.setApi("properties", 1, new WidgetProperties(widgetInstance));
            widgetInstance.setApi("interactions", 1, new WidgetInteractionsApi(widgetInstance));
            widgetInstance.setApi("preview", 1, new WidgetPreviewApi(widgetInstance));
            widgetInstance.setApi("innerfocus", 1, new WidgetInnerFocus(widgetInstance));
            this.insertPlaceholderImage(widgetInstanceElement, widgetInstance, isDarkTheme);
            let initializeWidgetFn = async () => {
                Trace.logEvent(dashboard, "WebAppOrchestratorInitializingWidgetCallback", 0 /* verbose */, {
                    appId: widgetInstance.widgetRegistration.appId,
                    providerId: widgetInstance.widgetRegistration.providerId,
                    widgetDefinitionId: widgetInstance.widgetDefinitionId,
                    widgetInstanceId: widgetInstance.widgetId,
                });
                widgetInstance.widgetState = "loading";
                // Now that API's have been set, signal completion
                widgetInstance.signalAPIInitialization();
                widgetInstance.addStateChangeListener((widgetInstance, previousState, currentState) => {
                    if (currentState === "loading") {
                        if (widgetInstanceElement.hasChildNodes() && widgetInstanceElement.lastChild) {
                            Trace.logEvent(dashboard, "ChildrenDeletedOnLoadingStateChange", 0 /* verbose */, {
                                appId: widgetInstance.widgetRegistration.appId,
                                providerId: widgetInstance.widgetRegistration.providerId,
                                widgetDefinitionId: widgetInstance.widgetDefinitionId,
                                widgetInstanceId: widgetInstance.widgetId,
                            });
                        }
                        this.insertPlaceholderImage(widgetInstanceElement, widgetInstance, isDarkTheme);
                    }
                });
                // Allow for an entryPoint override, currently based on geolocation, eg: for CN
                let entryPointKey = getGeoLocationCode();
                let entryPoint = getEntryPointOverride(widgetInstance.widgetRegistration, entryPointKey);
                if (entryPoint !== null) {
                    Trace.logEvent(dashboard, "InitializeWidgetEntryPointOverride", 0 /* verbose */, {
                        appId: widgetInstance.widgetRegistration.appId,
                        providerId: widgetInstance.widgetRegistration.providerId,
                        widgetDefinitionId: widgetInstance.widgetDefinitionId,
                        widgetInstanceId: widgetInstance.widgetId,
                        originalEntryPoint: widgetInstance.widgetRegistration.entryPoint,
                        overrideEntryPoint: entryPoint,
                        key: entryPointKey,
                    });
                }
                else {
                    entryPoint = widgetInstance.widgetRegistration.entryPoint;
                }
                // A non-existent host indicates that the widget is Outlook widget, and will be later loaded by the dashboard
                if (typeof entryPoint.host !== "undefined" && entryPoint.type !== "PLATFORM") {
                    await this.initializeOutlookWidgetAsync(widgetInstance, widgetInstanceElement, entryPoint.host);
                }
                if (typeof entryPoint.className !== "undefined") {
                    widgetInstanceElement.className = entryPoint.className;
                }
                if (typeof entryPoint.configuration !== "undefined") {
                    widgetInstanceElement.dataset.widget = JSON.stringify(entryPoint.configuration);
                }
            };
            widgetInstanceElement.initializeFn = initializeWidgetFn;
            resolve(widgetInstanceElement);
        });
        this._widgetHtmlElements.set(widgetInstance, widgetInitPromise);
        return widgetInitPromise;
    }
    insertPlaceholderImage(widgetInstanceElement, widgetInstance, isDarkTheme) {
        // Ensure that the instance element is empty before adding the placeholder
        while (widgetInstanceElement.hasChildNodes() && widgetInstanceElement.lastChild) {
            widgetInstanceElement.removeChild(widgetInstanceElement.lastChild);
        }
        const placeholderImg = PlaceholderImageElement.createPlaceholderImage(widgetInstance, widgetInstanceElement.id, isDarkTheme);
        placeholderImg && widgetInstanceElement.appendChild(placeholderImg);
    }
    async removeAttributeListener(widget) {
        var _a;
        // Remove the callback once we're done with it so we don't get updates from individual widgets loading.
        let instanceId = (_a = widget.instance) === null || _a === void 0 ? void 0 : _a.instanceId;
        if (instanceId !== undefined) {
            let listenerToken = this._peregrineLoadListeners.get(instanceId);
            if (listenerToken !== undefined) {
                widget.removeAttributeListener("data-widget-state", listenerToken);
            }
        }
    }
    fireEvent(name, widgetInstance) {
        for (let index = 0; index < this._eventListeners.length; index++) {
            if (this._eventListeners[index]) {
                this._eventListeners[index](name, widgetInstance);
            }
        }
    }
    async getAllWidgetInstances() {
        let widgetInstances;
        // In dynamic feed, ignore all peregrine widgets in the orchestrator since they will be managed by the feed
        widgetInstances = (await this.getWidgetInstances()).filter((widgetInstance) => typeof widgetInstance.widgetRegistration.entryPoint.host !== "undefined");
        const feedInstance = await this.getFeedInstance();
        return [...widgetInstances, feedInstance];
    }
}
WidgetOrchestratorImpl._listenderAddedForPCSFailure = false; // Use this to avoid add listener for PCS failure multiple times
// Singleton instance
let g_orchestratorInstance;
export async function getWidgetOrchestrator() {
    addMarkerToPageTimings("MEE.GetWidgetOrchestratorStart");
    if (!g_orchestratorInstance) {
        g_orchestratorInstance = new Promise(async (resolve) => {
            const [registrar, instanceManager] = await Promise.all([
                getWidgetRegistrarAsync(),
                getWidgetInstanceManager()
            ]);
            addMarkerToPageTimings("MEE.GetWidgetOrchestratorEnd");
            resolve(new WidgetOrchestratorImpl(instanceManager, registrar));
        });
    }
    return g_orchestratorInstance;
}
